import {
  useLocalBusinessConsent,
  storedBusinessRequest,
  persistBusinessRequest,
  isLocalConfirmationRequired,
  isDefiniteBusinessRejection,
} from '../../../hooks/useLocalBusinessConsent';
import type { LocalInitializationConsent } from '@pokerroom/types';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import type { FinancialCapabilities } from '@pokerroom/types';
import { api } from '../../../lib/api';
import { sessionVenueKey, useAuthStore } from '../../../store/auth.store';
import { useCurrentVenue } from '../../../hooks/useCurrentVenue';

export function useFinancialCapabilities(memberId: string) {
  const user = useAuthStore((s) => s.user),
    actions = useAuthStore((s) => s.allowedActions);
  const revision = useAuthStore((s) => s.authRevision);
  const sessionRevision = useAuthStore((s) => s.sessionRevision);
  const { venue } = useCurrentVenue();
  const can = (action: string) => !!user?.isSystemUser || !!actions?.includes(action);
  const query = useQuery({
    queryKey: ['member-financial-capabilities', user?.id, venue?.id, memberId, revision],
    queryFn: () => api.get<FinancialCapabilities>(`/members/${memberId}/financial-capabilities`),
    enabled:
      !!venue &&
      (can('member:adjust_points') ||
        can('member:charge_read') ||
        can('member:charge_confirm') ||
        can('member:zipa_transfer')),
    retry: false,
  });
  return {
    ...query,
    venue: venue as { id: string; name: string; code: string } | undefined,
    user,
    can,
    revision,
    identity: `${user?.id}:${venue?.id}:${memberId}`,
    formIdentity: JSON.stringify([
      user?.id,
      venue?.id,
      memberId,
      revision,
      sessionRevision,
      actions,
    ]),
  };
}
/** Persist the exact uncertain request. Authorization failures cannot prove it did not commit. */
export function useFinancialOperation<Input, Preview, Receipt>(
  memberId: string,
  endpoint: string,
  identity: string,
) {
  const key = `member-financial:${identity}:${endpoint}`;
  const localBusiness = useLocalBusinessConsent();
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  const auth = () => {
    const s = useAuthStore.getState();
    return JSON.stringify([
      s.user?.id,
      sessionVenueKey(s.accessToken),
      s.sessionRevision,
      s.allowedActions,
    ]);
  };
  const signature = auth();
  const currentScope = () => live.current && signature === auth();
  type Pending = Input & {
    operationKey: string;
    quoteHash: string;
    localInitializationConsent?: LocalInitializationConsent;
  };
  const [unreadable] = useState(() => {
    try {
      const saved = storedBusinessRequest<Pending>(key);
      return (
        !!saved &&
        !(typeof saved.body?.operationKey === 'string' && typeof saved.body?.quoteHash === 'string')
      );
    } catch {
      return true;
    }
  });
  const [pending, setPending] = useState<Pending | null>(() => {
    try {
      const p = storedBusinessRequest<Pending>(key)?.body;
      return p && typeof p.operationKey === 'string' && typeof p.quoteHash === 'string' ? p : null;
    } catch {
      return null;
    }
  });
  const [preview, setPreview] = useState<Preview | null>(null),
    [receipt, setReceipt] = useState<Receipt | null>(null);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const inFlight = useRef(false),
    generation = useRef(0);
  const client = useQueryClient();
  const journalScope = useAuthStore((s) =>
    JSON.stringify([s.user?.id, s.sessionRevision, sessionVenueKey(s.accessToken), memberId]),
  );
  const reset = () => {
    generation.current++;
    setPreview(null);
    setReceipt(null);
    setError('');
  };
  async function load(input: Input) {
    if (inFlight.current || pending || unreadable || !currentScope()) return;
    inFlight.current = true;
    setBusy(true);
    reset();
    const current = generation.current;
    try {
      const result = await api.post<Preview>(`/members/${memberId}/${endpoint}/preview`, input);
      if (currentScope() && current === generation.current) setPreview(result);
    } catch {
      setError('financial.previewFailed');
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  async function submit(body: Pending) {
    if (inFlight.current || unreadable || !currentScope()) return;
    inFlight.current = true;
    setBusy(true);
    let modern = !pending;
    let storing = false;
    try {
      const stored = storedBusinessRequest<Pending>(key);
      modern = !pending || !!stored?.modern;
      if (!pending || stored?.amendable) {
        const input = body as Pending & {
          sourceVenueId?: string;
          destinationVenueId?: string;
          reversesTransferId?: string;
        };
        if (!input.reversesTransferId) {
          const currentVenue = sessionVenueKey(useAuthStore.getState().accessToken);
          const other =
            input.sourceVenueId === currentVenue ? input.destinationVenueId : input.sourceVenueId;
          const consent = await localBusiness.ask(
            [memberId],
            endpoint === 'sales' ? 'SALE' : 'TRANSFER',
            other,
          );
          if (!consent || !currentScope()) {
            inFlight.current = false;
            setBusy(false);
            return;
          }
          body = { ...body, localInitializationConsent: consent };
        }
      }
      storing = true;
      if (modern) persistBusinessRequest(key, body);
      else sessionStorage.setItem(key, JSON.stringify(body));
    } catch {
      setError(storing ? 'financial.storageFailed' : 'localBusiness.blocked');
      inFlight.current = false;
      setBusy(false);
      return;
    }
    inFlight.current = true;
    setPending(body);
    setBusy(true);
    setError('');
    try {
      const result = await api.post<Receipt>(`/members/${memberId}/${endpoint}/confirm`, body);
      if (!currentScope()) return;
      sessionStorage.removeItem(key);
      setPending(null);
      setPreview(null);
      setReceipt(result);
      for (const queryKey of [
        ['zipa-journal', memberId, journalScope],
        ['member-zipa'],
        ['member-zipa-balances'],
        ['member-ledger-summary'],
        ['member-packages'],
        ['member-plans'],
        ['member-charges'],
        ['member-transfers'],
        ['zipa-daily-totals'],
        ['accounting-summary'],
      ])
        void client.invalidateQueries({ queryKey });
    } catch (failure) {
      const status = isAxiosError(failure) ? failure.response?.status : undefined;
      if (!currentScope()) return;
      if (isLocalConfirmationRequired(failure)) {
        if (modern) persistBusinessRequest(key, body, 'KNOWN_REJECTED');
        setError(modern ? 'localBusiness.reconfirm' : 'localBusiness.legacy');
      } else if (
        !pending &&
        isDefiniteBusinessRejection(failure, endpoint === 'sales' ? 'SALE' : 'TRANSFER')
      ) {
        sessionStorage.removeItem(key);
        setPending(null);
        setPreview(null);
        setError('financial.stale');
      } else setError(status === 403 ? 'financial.permissionRecovery' : 'financial.uncertain');
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  return {
    confirmation: localBusiness.confirmation,
    pending,
    preview,
    receipt,
    busy,
    error: unreadable ? 'financial.storageFailed' : error,
    locked: busy || !!pending || unreadable,
    reset,
    load,
    submit,
  };
}
